import React, { useMemo } from "react"
import { Building2, ShieldCheck, MapPin } from "lucide-react"
import { cn } from "@/lib/utils"

// ── Radial Match Score ────────────────────────────────────────────────────────
function MatchScore({ pct }: { pct: number }) {
  const deg = Math.round((pct / 100) * 360)
  return (
    <div
      className="relative size-[34px] shrink-0 rounded-full flex items-center justify-center group-hover:scale-105 transition-transform duration-300"
      style={{
        background: `conic-gradient(var(--primary) 0deg ${deg}deg, color-mix(in srgb, var(--primary) 18%, transparent) ${deg}deg 360deg)`,
      }}
      title={`${pct}% AI Match`}
    >
      <div className="absolute size-[26px] rounded-full bg-white flex items-center justify-center shadow-inner">
        <span className="text-[9px] font-bold text-slate-800">{pct}%</span>
      </div>
    </div>
  )
}

export interface SupplierCardProps {
  supplier?: any
  isMultiSelectActive?: boolean
  isSelected?: boolean
  isActiveTarget?: boolean
  onSelectToggle?: () => void
  onViewDetails?: () => void
  isLoading?: boolean
}

export function SupplierCard({
  supplier,
  isMultiSelectActive = false,
  isSelected = false,
  isActiveTarget = false,
  onSelectToggle = () => {},
  onViewDetails = () => {},
  isLoading = false,
}: SupplierCardProps) {
  if (isLoading) {
    return (
      <div className="relative overflow-hidden flex flex-col justify-between h-full rounded-[26px] bg-[#EEF1F5] dark:bg-slate-900/90 border border-slate-200/70 dark:border-slate-800 p-2.5 sm:p-3 gap-2.5 sm:gap-3 animate-pulse text-left shadow-xs">
        {/* Inset Floating Banner skeleton */}
        <div className="h-20 sm:h-22 w-full rounded-2xl bg-slate-200/80 dark:bg-slate-800 shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" />

        {/* Inner Body: Raised White Island skeleton */}
        <div className="rounded-2xl bg-white dark:bg-slate-800 shadow-[0_8px_22px_-4px_rgba(0,0,0,0.08),0_2px_6px_rgba(0,0,0,0.02)] border border-white/90 dark:border-slate-700/60 p-4 pt-2 flex flex-col flex-1 justify-between gap-2.5 relative">
          <div className="space-y-2.5">
            {/* Avatar skeleton */}
            <div className="-mt-10 ml-0.5 size-16 rounded-full bg-slate-300 shrink-0 relative" />

            <div className="space-y-2 mt-1">
              <div className="flex items-center justify-between">
                <div className="h-4 bg-slate-200 rounded-sm w-3/5" />
                <div className="size-8 rounded-full bg-slate-200" />
              </div>
              <div className="space-y-1 min-h-[38px] justify-start flex flex-col">
                <div className="h-3 bg-slate-200 rounded-sm w-2/5" />
                <div className="h-3 bg-slate-200 rounded-sm w-1/2" />
              </div>
            </div>

            <div className="space-y-1 pt-0.5">
              <div className="h-3 bg-slate-200 rounded-sm w-12" />
              <div className="space-y-1.5 min-h-[54px]">
                <div className="h-3 bg-slate-200 rounded-sm w-full" />
                <div className="h-3 bg-slate-200 rounded-sm w-4/5" />
                <div className="h-3 bg-slate-200 rounded-sm w-3/5" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 mt-auto">
            <div className="space-y-1 flex flex-col items-center">
              <div className="h-4 bg-slate-200 rounded-sm w-12" />
              <div className="h-2.5 bg-slate-200 rounded-sm w-8" />
            </div>
            <div className="space-y-1 flex flex-col items-center">
              <div className="h-4 bg-slate-200 rounded-sm w-14" />
              <div className="h-2.5 bg-slate-200 rounded-sm w-10" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const matchPercentage = supplier?.rank !== undefined ? Math.round(supplier.rank * 100) : null

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest("a") || target.closest("button") || target.closest("input[type='checkbox']")) return
    if (isMultiSelectActive) {
      onSelectToggle()
    } else {
      onViewDetails()
    }
  }

  const locationString =
    supplier?.location ||
    [supplier?.city, supplier?.state, supplier?.country].filter(Boolean).join(", ")

  const companyTypes = useMemo(() => {
    const raw = supplier?.companyType || supplier?.businessType || supplier?.natureOfBusiness
    if (!raw) return []
    if (Array.isArray(raw)) {
      return raw.map((s) => String(s).trim()).filter(Boolean)
    }
    if (typeof raw === "string") {
      return raw
        .split(/[,/|]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    }
    return []
  }, [supplier?.companyType, supplier?.businessType, supplier?.natureOfBusiness])

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "group relative isolate overflow-hidden flex flex-col justify-between h-full rounded-[26px] bg-[#EEF1F5] dark:bg-slate-900/90 cursor-pointer text-left font-sans p-2.5 sm:p-3 gap-2.5 sm:gap-3 transition-all duration-300",
        isActiveTarget || isSelected
          ? "border-2 border-primary shadow-sm"
          : "border border-slate-200/70 dark:border-slate-800 shadow-xs hover:shadow-md hover:border-primary/30"
      )}
    >
      {/* Inset Floating Banner with Aura Background */}
      <div className="h-20 sm:h-22 w-full rounded-2xl bg-slate-50 dark:bg-slate-900/70 border border-white/60 dark:border-slate-800 relative shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
        {/* Blurred Hero Aura Glow Background */}
        <div
          className="absolute -inset-x-6 -inset-y-6 pointer-events-none blur-lg sm:blur-xl opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out will-change-[transform,opacity]"
          style={{
            background: "var(--hero-aura-glow)",
          }}
          aria-hidden="true"
        />

        {/* Multi-select checkbox */}
        {isMultiSelectActive && (
          <div
            className="absolute top-2.5 right-2.5 z-30 bg-white/90 backdrop-blur-xs p-1 rounded-md shadow-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onSelectToggle}
              className="size-4 accent-primary cursor-pointer block"
            />
          </div>
        )}
      </div>

      {/* Inner Body: Raised White Island (Popped Out) */}
      <div className="rounded-2xl bg-white dark:bg-slate-800/95 shadow-[0_8px_22px_-4px_rgba(0,0,0,0.08),0_2px_6px_rgba(0,0,0,0.02)] border border-white/90 dark:border-slate-700/60 ring-1 ring-slate-900/[0.04] p-4 pt-2 flex flex-col flex-1 justify-between gap-2.5 relative">
        {/* Upper Content Group */}
        <div className="space-y-2.5">
          {/* Overlapping Avatar Logo */}
          <div className="-mt-10 ml-0.5 size-16 rounded-full shadow-md bg-white flex items-center justify-center overflow-hidden shrink-0 relative">
            {supplier?.logo ? (
              <img src={supplier.logo} alt={supplier.name} className="size-full object-cover" />
            ) : (
              <div className="size-full bg-slate-50 flex items-center justify-center text-primary group-hover:scale-105 transition-transform duration-300">
                <Building2 className="size-7 text-primary" />
              </div>
            )}
          </div>

          {/* Company Name & Percentage on Right */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <h3
                  className="text-[16px] font-bold text-slate-900 group-hover:text-primary transition-colors duration-200 truncate"
                  title={supplier?.name}
                >
                  {supplier?.name}
                </h3>
                {supplier?.type === "internal" && supplier?.verificationStatus === "APPROVED" && (
                  <ShieldCheck className="size-4 text-blue-500 shrink-0" />
                )}
              </div>

              {/* Subtitle: Type as pills in Row 1, Location in Row 2 */}
              <div className="mt-1 flex flex-col gap-1 min-w-0 min-h-[38px] justify-start">
                {/* Row 1: Type as pills */}
                {companyTypes.length > 0 && (
                  <div className="flex items-center gap-1.5 overflow-hidden">
                    {companyTypes.map((type) => (
                      <span
                        key={type}
                        className="inline-flex items-center px-1.5 py-[1px] rounded-full text-[10px] leading-tight font-medium bg-primary/[0.04] dark:bg-primary/10 text-primary/85 dark:text-blue-300 border border-primary/10 dark:border-primary/20 shrink-0 max-w-full truncate"
                        title={type}
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                )}

                {/* Row 2: Location */}
                {locationString && (
                  <div
                    className="flex items-center gap-1 text-[11.5px] text-slate-500 font-medium truncate"
                    title={locationString}
                  >
                    <MapPin className="size-3 text-slate-400 shrink-0" />
                    <span className="truncate">{locationString}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Percentage on Right of Name */}
            {matchPercentage !== null && <MatchScore pct={matchPercentage} />}
          </div>

          {/* About Section - Fixed position relative to company header */}
          <div className="space-y-1 pt-0.5">
            <span className="text-[11.5px] font-semibold text-slate-800 tracking-wider block">
              About
            </span>
            <p className="text-[12px] leading-[18px] text-slate-600 line-clamp-3 min-h-[54px]">
              {supplier?.description ||
                "Leading provider and supplier with verified quality standards and reliable global supply chain fulfillment."}
            </p>
          </div>
        </div>

        {/* Bottom: ESTD & Turnover */}
        <div className="grid grid-cols-2 gap-3 text-center pt-2 mt-auto min-h-[40px]">
          <div>
            {supplier?.yearEstablished ? (
              <>
                <div className="text-[15px] font-bold text-slate-900 tracking-tight">
                  {supplier.yearEstablished}
                </div>
                <div className="text-[10.5px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">
                  ESTD
                </div>
              </>
            ) : null}
          </div>
          <div>
            {supplier?.annualRevenue ? (
              <>
                <div
                  className="text-[15px] font-bold text-slate-900 tracking-tight truncate"
                  title={supplier.annualRevenue}
                >
                  {supplier.annualRevenue.replace(/\s*USD/i, "")}
                </div>
                <div className="text-[10.5px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">
                  Turnover
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
